import { api } from '../../app/api/baseApi';
import { RootState } from '../../app/store';
import { HIERARCHICAL_SORT_ORDER } from '../../constants';

const buildOrgHeaders = (organizationId?: string, realm?: string): { headers?: Record<string, string> } => {
    if (!organizationId && !realm) return {};
    return {
        headers: {
            ...(organizationId ? { organizationId } : {}),
            ...(realm ? { 'x-realm': realm } : {}),
        },
    };
};

export const groupApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getAllGroups: builder.mutation<any, any>({
            queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
                const state = getState() as RootState;
                const sortOption = state.common.sortOption;
                const { body, headers, parent } = args ?? {};

                const sortParams =
                    sortOption && sortOption !== HIERARCHICAL_SORT_ORDER
                        ? { sort: { key: 'name', sortType: sortOption } }
                        : {};

                const mergedBody = {
                    size: 5000,
                    page: 0,
                    ...body,
                    ...sortParams,
                };

                const result = await baseQuery({
                    url: 'iam/v1/groups/hierarchy',
                    method: 'POST',
                    body: mergedBody,
                    ...(parent && { params: { parent } }),
                    ...(headers && { headers }),
                });

                return result.data ? { data: result.data } : { error: result.error };
            },
        }),

        getAllGroupsForTariff: builder.mutation<any, any>({
            query: ({ headers, parentId }) => {
                return {
                    url: `ems/v1/assets/hierarchy`,
                    method: 'GET',
                    ...(parentId && { params: { parentId } }),
                    ...(headers && { headers }),
                };
            },
        }),

        getAllFlattenedGroups: builder.query({
            query: (body) => ({
                url: '/iam/v1/groups/details',
                body,
                method: 'POST',
            }),
        }),

        getGroupType: builder.query({
            query: () => ({
                url: '/iam/v1/groups/groupType',
                method: 'GET',
            }),
        }),

        getAssignedUsers: builder.query({
            query: (groupId) => ({
                url: `iam/v1/groups/${groupId}/users`,
                method: 'GET',
            }),
        }),

        createGroup: builder.mutation<object, object>({
            query: (body) => ({
                url: `/iam/v1/groups`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['Orgs'] : []),
        }),

        moveExistingGroupsAndDevices: builder.mutation<object, object>({
            query: (body) => ({
                url: `/iam/v1/groups`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['GroupById', 'GroupsHierarchy', 'DeviceList'] : []),
        }),
        updateGroup: builder.mutation<object, object>({
            query: (payload: {
                groupId: string;
                groupTypeId: string;
                name: string;
                shortName: string;
                parentId: string;
                lat: any;
                lng: any;
            }) => {
                return {
                    url: `/iam/v1/groups/${payload.groupId}`,
                    method: 'PATCH',
                    body: payload,
                };
            },
            invalidatesTags: (result, error): any => (!error ? ['GroupById'] : []),
        }),
        deleteGroup: builder.mutation({
            query: (id) => ({
                url: `/iam/v1/groups/${id}`,
                method: 'DELETE',
            }),
        }),
        updateGroupBackground: builder.mutation<object, object>({
            query: (payload: { groupId: string; image: any }) => ({
                url: `/iam/v1/groups/${payload.groupId}/image`,
                method: 'PATCH',
                body: payload.image,
            }),
            invalidatesTags: (result, error): any => {
                return !error ? ['GroupById'] : [];
            },
        }),

        getGroupDetails: builder.query({
            query: (args: string | undefined | { groupId?: string; organizationId?: string; realm?: string }) => {
                const groupId = typeof args === 'string' ? args : args?.groupId;
                const organizationId = typeof args === 'string' ? undefined : args?.organizationId;
                const realm = typeof args === 'string' ? undefined : args?.realm;
                return {
                    url: `iam/v1/groups/${groupId}`,
                    method: 'GET',
                    ...buildOrgHeaders(organizationId, realm),
                };
            },
            providesTags: ['GroupById'],
        }),
        assignUsersToGroups: builder.mutation<object, object>({
            query: (payload: { groupId: string; userIds: string[] }) => ({
                url: `/iam/v1/groups/${payload.groupId}/assign/users`,
                method: 'PATCH',
                body: { userIds: payload.userIds },
            }),
        }),
        removeGroupImage: builder.mutation<object, object>({
            query: (payload: { groupId: string }) => ({
                url: `/iam/v1/groups/${payload.groupId}/image`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error): any => {
                return !error ? ['GroupById'] : [];
            },
        }),

        updateGroupServiceContact: builder.mutation({
            query: ({ organizationId, realm, ...body }: any) => ({
                url: `iam/v1/groups/service-contact`,
                method: 'POST',
                body,
                ...buildOrgHeaders(organizationId, realm),
            }),
            invalidatesTags: (result, error): any => {
                return !error ? ['ServiceContact'] : [];
            },
        }),

        getGroupServiceContact: builder.query({
            query: (arg: any) => {
                const groupId = typeof arg === 'string' ? arg : arg?.groupId;
                const organizationId = typeof arg === 'object' ? arg?.organizationId : undefined;
                const realm = typeof arg === 'object' ? arg?.realm : undefined;
                return {
                    url: `iam/v1/groups/${groupId}/service-contact`,
                    method: 'GET',
                    ...buildOrgHeaders(organizationId, realm),
                };
            },
            providesTags: ['ServiceContact'],
        }),
        getFilteredHierarchy: builder.query({
            query: ({ body, headers, parent }) => {
                return {
                    url: `iam/v1/groups/filter`,
                    method: 'POST',
                    body: body ?? {
                        size: 10,
                        page: 0,
                    },
                    ...(parent && { params: { parent } }),
                    ...(headers && { headers }),
                };
            },
        }),
        moveServiceContact: builder.mutation({
            query: ({ body, organizationId, realm }: any) => ({
                url: 'iam/v1/groups/move/service-contact',
                method: 'PATCH',
                body,
                ...buildOrgHeaders(organizationId, realm),
            }),
            invalidatesTags: (result, error): any => {
                return !error ? ['ServiceContact'] : [];
            },
        }),
    }),
});

export const {
    useGetAssignedUsersQuery,
    useGetAllFlattenedGroupsQuery,
    useAssignUsersToGroupsMutation,
    useCreateGroupMutation,
    useUpdateGroupBackgroundMutation,
    useUpdateGroupMutation,
    useGetAllGroupsMutation,
    useGetAllGroupsForTariffMutation,
    useRemoveGroupImageMutation,
    useDeleteGroupMutation,
    useGetGroupDetailsQuery,
    useGetGroupTypeQuery,
    useMoveExistingGroupsAndDevicesMutation,
    useGetGroupServiceContactQuery,
    useGetFilteredHierarchyQuery,
    useUpdateGroupServiceContactMutation,
    useMoveServiceContactMutation,
    useLazyGetGroupDetailsQuery,
} = groupApi;
